import { useState, useMemo } from "react";
import BoxedLayout from "../../parts/layout/BoxedLayout";
import { StepIndicator } from "../../components/marketingContentBuilder/StepIndicator";
import { CampaignSetupStep } from "../../components/marketingContentBuilder/CampaignSetupStep";
import { ICPBuilderStep } from "../../components/marketingContentBuilder/ICPBuilderStep";
import { PainPointSelectionStep } from "../../components/marketingContentBuilder/PainPointSelectionStep";
import { ContentThemeStep } from "../../components/marketingContentBuilder/ContentThemeStep";
import { ContentFormatStep } from "../../components/marketingContentBuilder/ContentFormatStep";
import { AdvancedCustomizationStep } from "../../components/marketingContentBuilder/AdvancedCustomizationStep";
import { ContentReviewStep } from "../../components/marketingContentBuilder/ContentReviewStep";
import ChatComponent from "../../components/ai/ChatComponent";
import QuestionGenerator from "../../components/ai/QuestionGenerator";
import { FiArrowLeft, FiArrowRight, FiSend, FiEdit3 } from "react-icons/fi";

// Dynamic questions based on content format to avoid redundancy
const getQuestionsForTiles = (contentFormat) => {
    const baseQuestions = {
        'blog-post': [
            "Create LinkedIn posts based on this blog - make them engaging, professional snippets",
            "Generate a 3-email nurture sequence based on this blog content",
            "Create an executive summary or one-pager for sales teams"
        ],
        'white-paper': [
            "Create a blog post summary of this white paper - make it more accessible",
            "Generate LinkedIn thought leadership posts highlighting key insights",
            "Create an email campaign promoting this white paper as a lead magnet"
        ],
        'case-study': [
            "Create LinkedIn posts showcasing the success metrics from this case study",
            "Generate a blog post expanding on the lessons learned",
            "Create sales presentation slides based on this case study"
        ],
        'email-campaign': [
            "Create LinkedIn posts that complement this email campaign messaging",
            "Generate a landing page copy that aligns with this email campaign",
            "Create a blog post that supports the themes in this email campaign"
        ],
        'linkedin-posts': [
            "Create a detailed blog post expanding on these LinkedIn insights",
            "Generate an email campaign that drives engagement with these LinkedIn posts",
            "Create X (Twitter) posts with shorter versions of these LinkedIn insights"
        ],
        'x-twitter-posts': [
            "Create LinkedIn posts with expanded versions of these X insights",
            "Generate a blog post that elaborates on these X post themes",
            "Create an email campaign that builds on these X post messages"
        ],
        'sales-enablement': [
            "Create a blog post that educates prospects on the topics covered in these sales materials",
            "Generate LinkedIn posts that support the sales messaging in these materials",
            "Create an email sequence that nurtures leads toward these sales conversations"
        ]
    };

    return baseQuestions[contentFormat] || [
        "Create supporting social media content based on this material",
        "Generate an email campaign sequence based on this content",
        "Create a case study template based on this content"
    ];
};

function MarketingContentBuilder() {
    const [currentStep, setCurrentStep] = useState(0);
    const [showContentGeneration, setShowContentGeneration] = useState(false);
    const [question, setQuestion] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        // Campaign Setup
        vertical: '',
        audiencePersona: '',
        geographicFocus: '',
        country: '',
        campaignObjective: '',

        // ICP Builder
        companySize: '',
        revenueRange: '',
        geographicFootprint: '',
        technologyMaturity: '',
        growthStage: '',
        keyDecisionFactors: [],

        // Pain Point Selection
        selectedPainPoints: [],
        customPainPoints: [],

        // Content Theme Selection
        contentTheme: '',

        // Content Format Selection
        contentFormat: '',

        // Advanced Customization
        complianceMessaging: '',
        competitivePositioning: '',
        technicalDepth: '',
        callToAction: '',
        brandVoice: '',
        customBrandVoice: '',

        notes: '',
    });
    const [errors, setErrors] = useState({});

    const updateFormField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const toggleArrayField = (field, item) => {
        setFormData((prev) => {
            const currentArray = prev[field];
            return {
                ...prev,
                [field]: currentArray.includes(item)
                    ? currentArray.filter((i) => i !== item)
                    : [...currentArray, item],
            };
        });
    };

    const validateCurrentStep = () => {
        const newErrors = {};

        switch (currentStep) {
            case 0: // Campaign Setup
                if (!formData.vertical) {
                    newErrors.vertical = "Please select a business vertical";
                }
                if (!formData.audiencePersona) {
                    newErrors.audiencePersona = "Please select an audience persona";
                }
                break;
            case 3: // Content Theme
                if (!formData.contentTheme) {
                    newErrors.contentTheme = "Please select a content theme";
                }
                break;
            case 4: // Content Format
                if (!formData.contentFormat) {
                    newErrors.contentFormat = "Please select a content format";
                }
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        const isValid = validateCurrentStep();

        if (isValid) {
            setCurrentStep((prev) => Math.min(prev + 1, 6));
        }
    };

    const handleBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };


    const generateContentPrompt = (data) => {
        let prompt = `Write engaging, persuasive marketing content for TNS Complete Commerce payment solutions. `;

        // Content style guidelines - override for social media
        if (data.contentFormat === 'linkedin-posts') {
            prompt += `CRITICAL LINKEDIN POST REQUIREMENTS:
- Create professional LinkedIn posts (3-8 sentences each)
- Write multiple separate posts, NOT long-form content
- Each post must provide professional value and insights
- Use thoughtful, industry-expert tone
- Include relevant hashtags and encourage engagement
- Format as individual LinkedIn posts with clear structure
- Make each post shareable and discussion-worthy
- Focus on thought leadership and professional insights
            
            `;
        } else if (data.contentFormat === 'x-twitter-posts') {
            prompt += `CRITICAL X (TWITTER) POST REQUIREMENTS:
- Create SHORT, PUNCHY X posts (1-2 sentences each, under 280 characters)
- Write multiple separate posts, NOT long-form content
- Each post must be immediately impactful and attention-grabbing
- Use concise, powerful language
- Include relevant hashtags for discoverability
- Format as individual X posts optimized for quick consumption
- Make each post highly shareable and engaging
- Focus on quick insights and strong hooks
            
            `;
        } else {
            prompt += `CRITICAL STYLE REQUIREMENTS:
- Write in flowing, narrative style - NOT bullet points or lists
- Use compelling storytelling and emotional language
- Create smooth transitions between ideas
- Write in paragraphs with natural flow
- Avoid bullet points, numbered lists, or technical documentation style
- Use persuasive marketing language that builds desire
- Include specific benefits and outcomes, not just features
- Write like a professional marketing copywriter, not a technical writer
            
            `;
        }

        // Target context
        if (data.vertical) {
            prompt += `Target vertical: ${data.vertical} industry. `;
        }

        if (data.audiencePersona) {
            const personaContext = {
                'business-buyer': 'Focus on ROI, business outcomes, competitive advantages, and strategic value',
                'technical-buyer': 'Emphasize integration ease, technical capabilities, and implementation benefits while maintaining marketing appeal',
                'channel-partner': 'Highlight partnership opportunities, mutual benefits, and market expansion potential'
            };
            prompt += `Primary audience: ${data.audiencePersona} - ${personaContext[data.audiencePersona] || 'business decision makers'}. `;
        }

        // Geographic and market context
        if (data.geographicFocus) {
            prompt += `Market focus: ${data.geographicFocus}`;
            if (data.country) {
                prompt += ` (${data.country})`;
            }
            prompt += `. Address regional market needs and opportunities. `;
        }

        // ICP (Ideal Customer Profile) context
        let icpContext = [];
        if (data.companySize) {
            const sizeLabels = {
                'startup': 'startup companies (1-50 employees)',
                'smb': 'small to medium businesses (50-500 employees)',
                'enterprise': 'enterprise organizations (500+ employees)'
            };
            icpContext.push(`company size: ${sizeLabels[data.companySize] || data.companySize}`);
        }

        if (data.revenueRange) {
            const revenueLabels = {
                'under-1m': 'under $1M annual transaction volume',
                '1m-10m': '$1M-$10M annual transaction volume',
                '10m-100m': '$10M-$100M annual transaction volume',
                'over-100m': 'over $100M annual transaction volume'
            };
            icpContext.push(`transaction volume: ${revenueLabels[data.revenueRange] || data.revenueRange}`);
        }

        if (data.geographicFootprint) {
            const footprintLabels = {
                'single-location': 'single location operations',
                'regional': 'regional multi-location presence',
                'national': 'national footprint',
                'international': 'international operations'
            };
            icpContext.push(`business footprint: ${footprintLabels[data.geographicFootprint] || data.geographicFootprint}`);
        }

        if (data.technologyMaturity) {
            const techLabels = {
                'legacy': 'legacy technology infrastructure',
                'modern': 'modern technology stack',
                'digital-first': 'digital-first, cloud-native operations'
            };
            icpContext.push(`technology maturity: ${techLabels[data.technologyMaturity] || data.technologyMaturity}`);
        }

        if (data.growthStage) {
            const growthLabels = {
                'established': 'established business operations',
                'expanding': 'expanding market presence',
                'transforming': 'business transformation phase'
            };
            icpContext.push(`growth stage: ${growthLabels[data.growthStage] || data.growthStage}`);
        }

        if (data.keyDecisionFactors && data.keyDecisionFactors.length > 0) {
            icpContext.push(`key decision factors: ${data.keyDecisionFactors.join(', ')}`);
        }

        if (icpContext.length > 0) {
            prompt += `Target customer profile: ${icpContext.join('; ')}. Tailor messaging to resonate with these specific characteristics. `;
        }

        // Pain points - frame as challenges to overcome
        if (data.selectedPainPoints.length > 0 || data.customPainPoints.length > 0) {
            const allPainPoints = [...(data.selectedPainPoints || []), ...(data.customPainPoints || [])];
            prompt += `Key challenges to address: ${allPainPoints.join(', ')}. Transform these pain points into compelling narrative about how TNS solves real business problems. `;
        }

        // Content theme guidance
        const themeInstructions = {
            'agitate': 'Use problem-agitation approach: highlight the cost and consequences of current challenges, then position TNS as the solution. Create urgency around addressing these issues.',
            'educate': 'Use educational storytelling: teach valuable concepts while positioning TNS as the expert solution. Build authority through insights while maintaining marketing appeal.',
            'engage': 'Use collaborative approach: position TNS as a trusted partner. Focus on success stories, mutual benefits, and positive outcomes from working together.'
        };

        if (data.contentTheme && themeInstructions[data.contentTheme]) {
            prompt += `Content approach: ${themeInstructions[data.contentTheme]} `;
        }

        // Format-specific instructions
        const formatInstructions = {
            'blog-post': 'Create an engaging blog post with compelling headline, introduction hook, flowing body content, and strong conclusion with clear next steps.',
            'white-paper': 'Write authoritative yet accessible content that builds credibility while remaining engaging. Use strategic insights and thought leadership positioning.',
            'case-study': 'Tell a compelling success story with clear challenge-solution-results narrative. Focus on transformation and positive outcomes.',
            'email-campaign': 'Create persuasive email content with attention-grabbing subject lines and compelling calls-to-action. Build urgency and desire.',
            'linkedin-posts': 'Create professional LinkedIn posts (3-8 sentences each). Include thoughtful insights, industry expertise, and professional tone. Use relevant hashtags and encourage engagement. Each post should provide value and establish thought leadership.',
            'x-twitter-posts': 'Create short, punchy X (Twitter) posts (1-2 sentences each, under 280 characters). Be concise, impactful, and attention-grabbing. Use relevant hashtags and make each post highly shareable.',
            'sales-enablement': 'Create persuasive sales materials that help close deals. Focus on competitive advantages and customer benefits.'
        };

        if (data.contentFormat && formatInstructions[data.contentFormat]) {
            prompt += `Format requirements: ${formatInstructions[data.contentFormat]} `;
        }

        // Brand voice instructions
        const voiceInstructions = {
            'tns-voice': 'Write as a professional expert that removes complexity. Use authoritative yet approachable tone, solution-focused and partnership-oriented. Enterprise B2B with confidence-building clarity.',
            'authoritative': 'Write with confidence and expertise while remaining approachable. Position TNS as the industry leader.',
            'consultative': 'Use helpful, advisory tone that positions TNS as a trusted advisor solving business challenges.',
            'innovative': 'Emphasize cutting-edge technology and forward-thinking solutions. Create excitement about the future.',
            'collaborative': 'Focus on partnership and working together toward shared success. Emphasize mutual benefits.',
            'professional': 'Maintain business-focused, reliable tone while keeping content engaging and persuasive.'
        };

        if (data.brandVoice === 'custom' && data.customBrandVoice) {
            prompt += `Brand voice: ${data.customBrandVoice} `;
        } else if (data.brandVoice && voiceInstructions[data.brandVoice]) {
            prompt += `Brand voice: ${voiceInstructions[data.brandVoice]} `;
        }

        // Compliance messaging approach
        if (data.complianceMessaging) {
            const complianceInstructions = {
                'pci-focus': 'Emphasize PCI DSS compliance, security standards, and data protection benefits throughout the content.',
                'gdpr-focus': 'Highlight GDPR compliance, data privacy capabilities, and European data protection standards.',
                'regional-compliance': 'Address local regulatory requirements and compliance standards specific to the target market.',
                'no-compliance': 'Focus on general business benefits without emphasizing specific compliance frameworks.'
            };
            prompt += `Compliance approach: ${complianceInstructions[data.complianceMessaging] || 'balanced compliance messaging'}. `;
        }

        // Competitive positioning strategy
        if (data.competitivePositioning) {
            const competitiveInstructions = {
                'direct-comparison': 'Include direct comparisons with named competitors, highlighting TNS advantages and differentiators.',
                'category-leadership': 'Position TNS as the clear industry leader and market innovator without naming specific competitors.',
                'alternative-approach': 'Present TNS as a better approach and solution without directly mentioning competitor names.',
                'no-competitive': 'Focus exclusively on TNS benefits and capabilities without competitive references.'
            };
            prompt += `Competitive positioning: ${competitiveInstructions[data.competitivePositioning] || 'focus on TNS strengths'}. `;
        }

        // Technical depth guidance
        if (data.technicalDepth) {
            const techInstructions = {
                'high-level': 'Focus on business benefits and strategic value. Minimize technical jargon.',
                'moderate-detail': 'Include some technical concepts but explain them in business terms.',
                'deep-technical': 'Include technical details but frame them in terms of business value and competitive advantage.'
            };
            prompt += `Technical level: ${techInstructions[data.technicalDepth] || 'balanced approach'}. `;
        }

        // Campaign objective
        if (data.campaignObjective) {
            const objectiveInstructions = {
                'lead-generation': 'Create compelling content that drives action and generates qualified leads.',
                'education': 'Educate while building preference for TNS solutions.',
                'partner-enablement': 'Support channel partners with persuasive materials they can use.',
                'thought-leadership': 'Establish TNS as the industry expert and thought leader.'
            };
            prompt += `Campaign goal: ${objectiveInstructions[data.campaignObjective] || 'drive engagement'}. `;
        }

        // Call-to-action guidance
        if (data.callToAction) {
            prompt += `Include compelling call-to-action for: ${data.callToAction.replace('-', ' ')}. `;
        }

        prompt += `
        
FINAL REQUIREMENTS:
- Write in marketing copywriter style, not technical documentation
- Use paragraphs and flowing narrative, NO bullet points or lists
- Create emotional connection and build desire for TNS solutions
- Include specific TNS Complete Commerce capabilities naturally woven into the narrative
- End with compelling next steps that encourage action
- Make every sentence count toward persuasion and engagement
- Format the output in clean, readable markdown with proper headings, paragraphs, and emphasis
- Use markdown formatting: ## for main headings, **bold** for emphasis, and proper paragraph breaks
- Ensure the content flows smoothly with clear section breaks
        `;

        return prompt;
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            console.log('Marketing Content Builder submitted:', formData);

            const generatedPrompt = generateContentPrompt(formData);
            console.log('📝 Setting question in MarketingContentBuilder:', generatedPrompt.substring(0, 100) + '...');
            setQuestion(generatedPrompt);

            setShowContentGeneration(true);

        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to generate content. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleQuestionSelect = (questionText) => {
        console.log('🎯 handleQuestionSelect called with:', questionText);
        console.log('🎯 Current question state:', question.substring(0, 100) + '...');
        setQuestion(questionText + " " + question);
    };

    const handleBackToForm = () => {
        setShowContentGeneration(false);
        setQuestion("");
        setIsSubmitting(false);
    };

    const steps = useMemo(() => [
        <CampaignSetupStep
            key="campaign"
            formData={formData}
            onChange={updateFormField}
            errors={errors}
        />,
        <ICPBuilderStep
            key="icp"
            formData={formData}
            onChange={updateFormField}
            onToggleArray={toggleArrayField}
            errors={errors}
        />,
        <PainPointSelectionStep
            key="painpoints"
            formData={formData}
            onChange={updateFormField}
            onToggleArray={toggleArrayField}
        />,
        <ContentThemeStep
            key="theme"
            formData={formData}
            onChange={updateFormField}
            errors={errors}
        />,
        <ContentFormatStep
            key="format"
            formData={formData}
            onChange={updateFormField}
            errors={errors}
        />,
        <AdvancedCustomizationStep
            key="advanced"
            formData={formData}
            onChange={updateFormField}
        />,
        <ContentReviewStep key="review" data={formData} />,
    ], [formData, errors]);

    if (showContentGeneration) {
        return (
            <BoxedLayout
                content={
                    <>
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                                <FiEdit3 className="w-4 h-4 mr-2" />
                                AI-Powered Marketing Content Builder
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                                Generate your
                                <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent"> marketing content</span>
                            </h1>
                        </div>

                        <div className="mb-4">
                            <button
                                onClick={handleBackToForm}
                                className="flex items-center px-4 py-2 bg-gray-500 text-white hover:bg-white hover:text-gray-600 transition-colors border border-gray-400 rounded-full text-sm font-medium"
                            >
                                <FiArrowLeft className="w-4 h-4 mr-2" />
                                Back to Content Builder
                            </button>
                        </div>

                        <div className="w-full max-w-5xl bg-white bg-opacity-80 p-6 rounded-lg shadow-lg">
                            <QuestionGenerator
                                questions={getQuestionsForTiles(formData.contentFormat)}
                                onQuestionSelect={handleQuestionSelect}
                            />
                            <ChatComponent
                                question={question}
                                region={formData.geographicFocus || 'usa'}
                            />
                        </div>
                    </>
                }
            />
        );
    }

    return (
        <BoxedLayout
            content={
                <>
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <FiEdit3 className="w-4 h-4 mr-2" />
                            AI-Powered Marketing Content Builder
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                            Create targeted
                            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent"> marketing content</span>
                        </h1>
                        <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
                            Generate compelling marketing materials for TNS Complete Commerce solutions with AI-powered content tailored to your target audience and business vertical.
                        </p>
                    </div>

                    <StepIndicator currentStep={currentStep} totalSteps={7} />

                    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                        {steps[currentStep]}
                    </div>

                    <div className="flex justify-between">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 0}
                            className={`flex items-center px-6 py-3 rounded-lg ${currentStep === 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                }`}
                        >
                            <FiArrowLeft className="w-5 h-5 mr-2" />
                            Back
                        </button>

                        <button
                            onClick={currentStep === steps.length - 1 ? handleSubmit : handleNext}
                            disabled={isSubmitting}
                            className={`flex items-center px-6 py-3 rounded-lg ${isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-green-600 hover:bg-green-700'
                                } text-white`}
                        >
                            {currentStep === steps.length - 1 ? (
                                <>
                                    {isSubmitting ? 'Generating...' : 'Generate Content'}
                                    <FiSend className="w-5 h-5 ml-2" />
                                </>
                            ) : (
                                <>
                                    Next
                                    <FiArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </button>
                    </div>
                </>
            }
        />
    );
}

export default MarketingContentBuilder;